import type { IFeedItem } from '../FeedItem/FeedItem';
import {
  countStat,
  FEED_STATS,
  type FeedStatFamily,
  type FeedStatKey,
} from './feedStatus';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useAuth } from '@presov.sk/presov-auth-react';
import { Box, ButtonBase, Skeleton } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Sila tintu zapnutej bunky, po rodinách - nie jedno číslo pre všetky. Modrá
 * z manuálu je zo štvorice najsvetlejšia a pri rovnakej sile by na bielej ešte
 * splývala; červená a zelená sú tmavšie a unesú menej. Je to ten istý prístup
 * ako pri štítkoch stavov v administrácii ([[tintedChip]]).
 *
 * Nepoužíva sa `<rodina>.surface` z témy: tie tinty sú ladené pre štítok na
 * sivom plátne a bunka stojí na bielej, kde zmiznú - viď komentár pri
 * [[FEED_STATS]].
 */
const ACTIVE_TINT: Record<FeedStatFamily, number> = {
  primary: 0.14,
  blue: 0.22,
  green: 0.18,
  yellow: 0.3,
};

/**
 * Plocha zapnutej bunky. Dizajn hovorí výslovne „nie čierna", takže je to tichý
 * tint vlastnej farby stavu; „Moje" farbu nemá, tomu ostáva sivá.
 */
const activeSurface = (theme: Theme, family?: FeedStatFamily) =>
  family
    ? alpha(theme.palette[family].main, ACTIVE_TINT[family])
    : theme.palette.grey[200];

/**
 * Text zapnutej bunky. `extraDark`, nie `main` ani `dark`: label má 11px a na
 * vlastnom tinte by `main` spadol pod AA (modrá by mala 2,0:1, zelená 2,9:1).
 */
const activeText = (theme: Theme, family?: FeedStatFamily) =>
  family ? theme.palette[family].extraDark : theme.palette.grey[700];

type Props = {
  /**
   * Zoznam, z ktorého sa počítajú **čísla**. Zámerne nie ten, ktorý sa
   * filtruje: keď je v okolí 34 nových podnetov, je ich 34 aj vtedy, keď mám
   * zapnuté „Moje". Bunka je zároveň filter aj štatistika, a štatistika, ktorá
   * sa mení podľa vlastného filtra, nemeria nič.
   */
  countItems: IFeedItem[];
  value: FeedStatKey[];
  /**
   * Hlási sa **prepnutá bunka**, nie nový zoznam. Keby komponent skladal nový
   * zoznam z `value`, dve rýchle kliknutia po sebe by obe počítali z tej istej
   * hodnoty z posledného vykreslenia a druhé by prvé prepísalo - filter by sa
   * potichu nezapol.
   */
  onToggle: (key: FeedStatKey) => void;
  /** menšie bunky pre mobilný sheet */
  compact?: boolean;
  /**
   * Prvé načítanie okolia. Miesto čísel sú vtedy skeletony: nula je platná
   * hodnota („v okolí nič nie je"), takže kým dáta nie sú, nesmie sa kresliť -
   * po F5 to vyzeralo, že sa okolie vyprázdnilo, a o chvíľu čísla naskočili.
   */
  loading?: boolean;
};

/**
 * Druhý riadok panela: filtre, ktoré zároveň ukazujú, koľko je čoho.
 *
 * Počíta sa **z načítaného zoznamu**, nie z API - je to prehľad okolia, ktoré
 * má používateľ pred sebou na mape, nie súčet za celé mesto. Preto sa čísla
 * menia pri posune mapy a preto na ne netreba ďalší dotaz.
 *
 * Bunky sú zaškrtávacie a nezávislé: dá sa zapnúť viac naraz a zapnutie
 * všetkých vyjde na to isté ako nezapnutie žiadnej - viď [[matchesStats]].
 */
const FeedStats: React.FC<Props> = ({
  countItems,
  value,
  onToggle,
  compact = false,
  loading = false,
}) => {
  const { authUser } = useAuth();

  // Neprihlásený nemá „Moje" čo obsahovať a prázdna bunka s nulou vyzerá ako
  // chyba, nie ako prázdny stav.
  const stats = React.useMemo(
    () => FEED_STATS.filter((stat) => stat.key !== 'mine' || !!authUser),
    [authUser],
  );

  const counts = React.useMemo(
    () =>
      stats.map((stat) => ({
        ...stat,
        count: countStat(countItems, stat.key),
      })),
    [countItems, stats],
  );

  return (
    <Box
      sx={(theme) => ({
        flex: 'none',
        display: 'flex',
        overflow: 'hidden',
        borderRadius: `${theme.typography.pxToRem(12)}`,
        border: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
      })}
    >
      {counts.map((stat, index) => {
        const active = value.includes(stat.key);

        return (
          <ButtonBase
            key={stat.key}
            role='checkbox'
            aria-checked={active}
            disabled={loading}
            onClick={() => onToggle(stat.key)}
            sx={(theme) => ({
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              gap: '2px',
              height: theme.typography.pxToRem(compact ? 58 : 62),
              padding: theme.spacing(0, compact ? 1.125 : 1.5),
              textAlign: 'left',
              borderLeft:
                index === 0
                  ? 'none'
                  : `${theme.typography.pxToRem(1)} solid ${
                      theme.palette.divider
                    }`,
              // Zapnutá bunka je tichá plocha svojho stavu, nie sivá
              // s podčiarknutím: v paneli sa čierna nepoužíva a podčiarknutie
              // by pri viacerých zapnutých vyzeralo ako rozdelený riadok.
              // Zapnutie nesie plocha **aj** stmavnutie labelu - plocha sama
              // musí byť tichá, a tichá plocha na bielej je slabý signál.
              backgroundColor: active
                ? activeSurface(theme, stat.family)
                : theme.palette.background.paper,
              '&:hover': {
                backgroundColor: active
                  ? activeSurface(theme, stat.family)
                  : theme.palette.grey[50],
              },
            })}
          >
            {loading ? (
              <Skeleton
                variant='rounded'
                width={compact ? 20 : 24}
                height={compact ? 19 : 22}
              />
            ) : (
              <Box
                component='span'
                sx={(theme) => ({
                  fontSize: theme.typography.pxToRem(compact ? 19 : 22),
                  fontWeight: 700,
                  lineHeight: 1,
                })}
              >
                {stat.count}
              </Box>
            )}
            <Box
              component='span'
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: theme.typography.pxToRem(compact ? 5 : 6),
                fontSize: theme.typography.pxToRem(11),
                fontWeight: 500,
                whiteSpace: 'nowrap',
                color: active
                  ? activeText(theme, stat.family)
                  : theme.palette.grey[500],
              })}
            >
              {stat.family ? (
                <Box
                  component='span'
                  sx={{
                    flex: 'none',
                    width: 6,
                    height: 6,
                    borderRadius: '999px',
                    backgroundColor: `${stat.family}.main`,
                  }}
                />
              ) : null}
              {stat.label}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default FeedStats;
